import { ICustomerCaseData } from 'types/customer-case';

import CoinInHandIcon from 'assets/customer-cases/coin-in-hand.svg';
import CentrePointIcon from 'assets/customer-cases/centre-point.svg';
import StretchToolIcon from 'assets/customer-cases/stretch-tool.svg';
import { colors } from 'styles';
import Typography from '@mui/material/Typography/Typography';

import BlueShipImage from 'assets/customer-cases/blue-ship.webp';

import CityViewImage from 'assets/customer-cases/honda-marine/city-view.webp';
import BoatInPortImage from 'assets/customer-cases/honda-marine/boat-in-port.webp';
import BoatWithLinesImage from 'assets/customer-cases/honda-marine/boat-with-lines.webp';
import BridgeImage from 'assets/customer-cases/honda-marine/bridge.webp';
import HeroImage from 'assets/applications/first/hero.webp';

import WhiteFishingBoatIcon from 'assets/industries/fishing-boat.svg';
import WhiteCheckIcon from 'assets/white-check.svg';
import WhiteBrainIcon from 'assets/white-brain.svg';
import WhiteAgileIcon from 'assets/white-agile.svg';

import WhiteFloatIcon from 'assets/industries/float.svg';
import WhiteDoubleCoinIcon from 'assets/white-double-coin.svg';
import WhiteCircleAroundIcon from 'assets/white-circle-around.svg';
import WhiteArrowUpIcon from 'assets/white-arrow-up.svg';
import { intelligentSystemTestingApplicationMeta } from 'constants/pages';

export const secondCustomerCaseData: ICustomerCaseData = {
  heroData: {
    heroTitle: 'Validate AI Auto-Berthing for Leisure Boats',
    heroDescription:
      'Learn how a leading maritime OEM validated an AI-powered auto-docking system using high-fidelity simulation By replacing a costly field campaign with virtual testing, the OEM saved tens of thousands of euros in logistics, staffing, and travel. Simulation enabled faster iterations and safer early-stage validation of autodocking scenarios across diverse marina environments—all without leaving the lab.',
    heroListTitle: 'Redefining Autonomous Docking through Simulation',
    buttonLabel: 'Explore Maritime Industry',
    buttonLink: '/Industries/maritime',
    heroListItems: [
      {
        description:
          'By using AILiveSim, our customer avoided the need to send an 8-person team into the field for a week, resulting in significant cost savings and better use of engineering resources.',
        Icon: CoinInHandIcon,
        title: 'Significant Cost Savings',
      },
      {
        description:
          'Development was accelerated, and early-stage tests became safer, faster, and easier to control.',
        Icon: CentrePointIcon,
        title: 'Accelerated Development',
      },
      {
        description:
          'The experience demonstrated the value of simulation for reducing dependency on real-world testing and gave the maritime OEM a path to scale future developments more efficiently.',
        Icon: StretchToolIcon,
        title: 'Scalable Innovation',
      },
    ],
    heroImages: {
      firstImage: {
        imageSrc: CityViewImage,
        altText: 'City view with boat on the water',
      },
      secondImage: {
        imageSrc: BoatInPortImage,
        altText: 'Boat in port',
      },
      thirdImage: {
        imageSrc: BoatWithLinesImage,
        altText: 'Boat with lines',
      },
      fourthImage: {
        imageSrc: BridgeImage,
        altText: 'Bridge',
      },
    },
  },
  customerCase: {
    title: 'The Challenge: Testing Autodocking Systems in Real Conditions',
    description: (
      <>
        <Typography fontSize="18px" color={colors.mainDark}>
          Our OEM customer wanted to develop an automatic berthing system to enhance docking
          precision and safety for leisure boat operators. Real-world validation of autonomous
          docking systems presents substantial operational and financial challenges.
        </Typography>

        <Typography fontSize="18px" color={colors.mainDark} marginTop="16px">
          The core challenge centered on generating comprehensive edge cases - emergency vessels,
          floating debris, crowded marinas, and unpredictable human behavior. Virtual testing
          emerged as the solution - eliminating live testing dependencies while expanding testable
          scenarios
        </Typography>
      </>
    ),
    bottomText:
      'To accelerate development while controlling costs, the maritime OEM required a comprehensive simulation platform.',
    image: BlueShipImage,
    items: [
      'A typical campaign required 8 people on-site for a full week',
      'High costs for travel, vessel rental, and logistics',
      'Unpredictable conditions and weather-dependent testing',
      'Difficulty replicating consistent scenarios',
      'Safety concerns during early maneuver trials',
      'Long lead times and slow iteration cycles',
    ],
    link: '',
  },
  firstInformationBlockData: {
    title: 'The Solution: Integrating AILiveSim for Intelligent Autodocking Development',
    description: `The maritime OEM's legacy ROS simulator proved inadequate for their evolving autonomous navigation requirements, lacking the sophisticated physics modeling and environmental simulation capabilities needed for comprehensive virtual testing. This limitation, combined with the complete absence of hardware simulation infrastructure, forced the company to rely heavily on weather-dependent field trials that created unpredictable validation schedules and increased development costs.`,
    items: [
      {
        Icon: WhiteFishingBoatIcon,
        description:
          'True-to-life marine physics for autonomous docking with double-twin marina configurations.',
        title: 'High-Fidelity Simulation',
      },
      {
        Icon: WhiteAgileIcon,
        description: 'Risk-free scenario testing with instant reset capabilities.',
        title: 'Iteration Without Risk',
      },
      {
        Icon: WhiteBrainIcon,
        description: 'Laboratory-based AI development, bypassing costly marine operations.',
        title: 'Remote Efficiency',
      },
      {
        Icon: WhiteCheckIcon,
        description: 'Continuous technical expertise and simulation optimization.',
        title: 'On-Demand Support',
      },
    ],
  },
  resultsData: {
    title: 'Results: Scalable Simulation for Smarter Testing',
    description: `
      By implementing AILiveSim, the OEM eliminated the need for costly field trials involving an 8-person team, significantly reducing operational overhead. Simulation replaced unpredictable testing conditions with controlled, repeatable scenarios — accelerating development, improving early-stage safety, and laying the groundwork for future scalability.

      A major turning point came when the team realized their legacy system lacked flexibility and couldn’t manage complex scenarios. AILiveSim's user-friendly interface and multi-scenario testing convinced the team to commit to implementation, shifting from theoretical planning to real-world control validation via proof-of-concept and sensor integration.
    `,
    clientFeedbackMessage: `“Simulation allowed us to iterate quickly, validate safely, and deliver an intelligent co-captain experience the market hadn’t seen before.”`,
    clientLabel: '— Michael Grant, Innovation Lead at a major maritime OEM',
    listTitle: 'Key Outcomes',
    listItems: [
      <li key="1" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Replaced physical trials with controlled simulation, enabling faster development cycles
      </li>,
      <li key="2" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Improved test safety in early phases through risk-free virtual environments
      </li>,
      <li key="3" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Validated real-time control algorithms using simulated sensor inputs
      </li>,
      <li key="4" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Achieved greater flexibility and decision-making capabilities through multi-scenario
        functionality
      </li>,
      <li key="5" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Built a future-proof testing workflow for continuous innovation
      </li>,
    ],
    resultTitle: 'Reduce Costs and Risks with Advanced Simulation Technology',
    resultDescription:
      'Avoided a full-week field deployment of an 8-person team — saving significantly on travel, vessel rental, logistics, and engineering time.',
  },
  secondInformationBlockData: {
    title: 'Unlocking Scalable Innovation in Leisure Boating',
    description: `AILiveSim enabled the OEM to replace costly, unpredictable field testing with lab-based, repeatable simulation. This approach accelerated validation, enhanced early-stage safety, and set the foundation for scalable innovation. From initial scenario scripting to sensor integration and control algorithm testing, each phase brought the team closer to reliable autonomous operation — without ever leaving shore.`,
    items: [
      {
        Icon: WhiteDoubleCoinIcon,
        description:
          'Eliminated the need for in-field data collection and reduced reliance on expensive marine operations.',
        title: 'Significant Cost Savings',
      },
      {
        Icon: WhiteCircleAroundIcon,
        description:
          'Enabled repeatable, lab-based testing with automated simulation runs for quicker iteration and refinement.',
        title: 'Faster Development Cycles',
      },
      {
        Icon: WhiteFloatIcon,
        description:
          'Allowed safe validation of early-stage features and edge cases without physical risk to vessels or personnel.',
        title: 'Enhanced Safety During Testing',
      },
      {
        Icon: WhiteArrowUpIcon,
        description:
          'Delivered a flexible and scalable simulation workflow to support long-term growth and advanced autonomy development.',
        title: 'Future-Ready Platform',
      },
    ],
  },
  applicationData: {
    bottomText: '',
    description:
      'Train smarter with synthetic data that captures real-world complexity, delivering the diversity and accuracy your AI models need to perform reliably in any environment.',
    image: HeroImage,
    items: [],
    title: 'Learn More About Optimizing the Volume of Tests with Simulation in the Loop',
    link: intelligentSystemTestingApplicationMeta.url,
  },
};
